import React, { useState } from 'react'
import SearchComponent from '../components/SearchComponent';
import { Dropdown } from 'react-bootstrap';
import AdminLayout from '../../../layouts/AdminLayout';
import CreateAgentDialog from '../components/CreateAgentDialog';
import useIndexAgent from '../../../services/agent/hooks/useIndexAgent';
import { useDeleteAgent } from '../../../services/agent/hooks/useDeleteAgent';
import { AgentData } from '../../../services/agent/response/IndexAgentResponse';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import EditAgentDialog from '../components/EditAgentDialog';
import FieldColorComponent from '../components/FieldColorComponent';
import OtaChannelTable from '../components/OtaChannelTable';
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';
import PropertyInactiveBanner from '../../../shared/components/PropertyInactiveBanner';

const AgentPage: React.FC = () => {
    const [page, setPage] = useState<number>(1);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const [show, setShow] = useState<boolean>(false);

    const [editState, setEditState] = useState<boolean>(false);
    const [agentIdWillEdit, setAgentIdWillEdit] = useState<string>('');

    const { data: agentsData, isSuccess, isError, isLoading } = useIndexAgent({
        search: searchQuery,
        page: page,
        limit: 10,
    });

    const { mutate, isPending } = useDeleteAgent();

    const queryClient = useQueryClient();

    const onRemove = (data: AgentData) => {
        toast.loading('Removing agent...', { id: 'remove-bank-account' });

        mutate({ id: data.id }, {
            onSuccess: async () => {
                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['agents'].includes(key as string))
                });

                toast.success('Agent removed successfully', { id: 'remove-bank-account' });
            },
            onError: (mutationError) => {
                toast.error(mutationError?.message || 'Failed to remove agent', { id: 'remove-bank-account' });
            }
        });
    }

    const handleOpenUpdate = (agentId: string) => {
        setAgentIdWillEdit(agentId);
        setEditState(true);
    }

    const handleCloseUpdate = () => {
        setAgentIdWillEdit('');
        setEditState(false);
    }

    return (
        <AdminLayout>
            <div className="container-fluid mt-5">
                <div className="table-container min-vh-100">
                    <h3 className="mb-3">Agents</h3>
                    <PropertyInactiveBanner />
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3">
                        <SearchComponent queryKey={searchQuery} onSearch={setSearchQuery} />
                        <RequireAuth requiredRoles={['dev']} requiredPermissions={['agent_create']} asComponent>
                            <ReadOnlyGuard>
                                <button className="btn btn-primary" onClick={() => setShow(true)}>
                                    Create
                                </button>
                            </ReadOnlyGuard>
                        </RequireAuth>
                    </div>
                    <div className="agent-table-wrapper">
                        <table className="table table-striped d-none d-md-table">
                            <thead>
                                <tr>
                                    <th>Agent Name</th>
                                    <th>Assigned Color</th>
                                    <th>Website</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th className='text-end'>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isError && <tr><td colSpan={5}>Failed to load data.</td></tr>}
                                {isLoading && <tr><td colSpan={5}>Loading...</td></tr>}
                                {isSuccess && !isLoading && agentsData.data.length > 0 && agentsData.data.map((agent, idx) => (
                                    <tr key={idx}>
                                        <td>{agent.name}</td>
                                        <td>
                                            <ReadOnlyGuard>
                                                <FieldColorComponent agent={agent} />
                                            </ReadOnlyGuard>
                                        </td>
                                        <td>{agent.website ?? 'not set'}</td>
                                        <td>{agent.email ?? 'not set'}</td>
                                        <td>{agent.phone ?? 'not set'}</td>
                                        <td className="text-end">
                                            <Dropdown>
                                                <Dropdown.Toggle variant="default" id="dropdownMenuButton1">
                                                    Actions
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['agent_update']} asComponent>
                                                        <ReadOnlyGuard mode="hide">
                                                            <Dropdown.Item href="#" onClick={() => handleOpenUpdate(agent.id)}>Edit</Dropdown.Item>
                                                        </ReadOnlyGuard>
                                                    </RequireAuth>
                                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['agent_delete']} asComponent>
                                                        <ReadOnlyGuard mode="hide">
                                                            <Dropdown.Item href="#" onClick={() => onRemove(agent)} className='text-danger' disabled={isPending}>
                                                                Remove
                                                            </Dropdown.Item>
                                                        </ReadOnlyGuard>
                                                    </RequireAuth>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Tampilan Card untuk Layar Mobile */}
                        <div className="d-md-none">
                            {isError && <div className="text-center my-4">Failed to load data.</div>}
                            {isLoading && <div className="text-center my-4">Loading...</div>}
                            {isSuccess && !isLoading && agentsData.data.length > 0 && agentsData.data.map((agent, idx) => (
                                <div key={idx} className="card mb-3">
                                    <div className="card-body">
                                        <h5 className="card-title">{agent.name} <ReadOnlyGuard><FieldColorComponent agent={agent} /></ReadOnlyGuard></h5>
                                        <p className="card-text mb-1"><strong>Website:</strong> {agent.website ?? 'not set'}</p>
                                        <p className="card-text mb-1"><strong>Email:</strong> {agent.email ?? 'not set'}</p>
                                        <p className="card-text mb-1"><strong>Phone:</strong> {agent.phone ?? 'not set'}</p>
                                        <div className="d-flex justify-content-end mt-3">
                                            <Dropdown>
                                                <Dropdown.Toggle variant="default" id={`dropdownMenuButton-${idx}`}>Actions</Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['agent_update']} asComponent>
                                                        <ReadOnlyGuard mode="hide">
                                                            <Dropdown.Item href="#" onClick={() => handleOpenUpdate(agent.id)}>Edit</Dropdown.Item>
                                                        </ReadOnlyGuard>
                                                    </RequireAuth>
                                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['agent_delete']} asComponent>
                                                        <ReadOnlyGuard mode="hide">
                                                            <Dropdown.Item href="#" onClick={() => onRemove(agent)} className='text-danger' disabled={isPending}>Remove</Dropdown.Item>
                                                        </ReadOnlyGuard>
                                                    </RequireAuth>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {isSuccess && !isLoading && agentsData.data.length === 0 && (
                                <div className="text-center my-4">No data available.</div>
                            )}
                        </div>
                    </div>
                    {isSuccess && !isLoading && agentsData.data.length !== 0 && agentsData.pagination && (
                        <PaginationComponent
                            totalPages={agentsData.pagination.last_page}
                            currentPage={agentsData.pagination.current_page}
                            onPageChange={(page) => setPage(page)}
                        />
                    )}
                    <OtaChannelTable />
                </div>
            </div>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['agent_create']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <CreateAgentDialog show={show} onClose={() => setShow(false)} />
                </ReadOnlyGuard>
            </RequireAuth>
            {editState && agentIdWillEdit && <RequireAuth requiredRoles={['dev']} requiredPermissions={['agent_update']} asComponent><ReadOnlyGuard mode="hide"><EditAgentDialog show={editState} onClose={handleCloseUpdate} agentId={agentIdWillEdit} /></ReadOnlyGuard></RequireAuth>}
        </AdminLayout>
    )
}

export default AgentPage